import crypto from "node:crypto";
import http from "node:http";
import readline from "node:readline";
import open from "open";
import {
  saveAuth,
  generatePkce,
  OAUTH_AUTHORIZE_URL,
  OAUTH_TOKEN_URL,
  OAUTH_CLIENT_ID,
} from "../lib/auth.js";

const CALLBACK_PORT = 19823;
const LOCAL_REDIRECT_URI = `http://localhost:${CALLBACK_PORT}/callback`;
const HOSTED_REDIRECT_URI = "https://app.clawnify.com/oauth/cli";
const WEB_ORIGIN = "https://app.clawnify.com";

interface AuthCode {
  code: string;
  redirectUri: string; // the redirect_uri the code was issued for
}

/**
 * Waits for the authorization code from whichever source responds first:
 * - the local server receiving the OAuth redirect directly (the browser
 *   we opened uses the localhost redirect_uri — classic seamless flow)
 * - the local server receiving a notify fetch from the hosted /oauth/cli
 *   page (user opened the printed URL in a same-machine browser)
 * - the "Paste code here" prompt (any browser, e.g. over SSH)
 *
 * The two flows use distinct `state` values, which is how we know which
 * redirect_uri to use in the token exchange.
 */
function waitForCode(stateLocal: string, stateRemote: string): Promise<AuthCode> {
  return new Promise((resolve, reject) => {
    let done = false;
    let rl: readline.Interface | null = null;

    const server = http.createServer((req, res) => {
      const url = new URL(req.url!, `http://127.0.0.1:${CALLBACK_PORT}`);

      // Chrome sends a private-network CORS preflight before the https
      // page may fetch a 127.0.0.1 URL.
      const corsHeaders: Record<string, string> = {
        "Access-Control-Allow-Origin": WEB_ORIGIN,
        "Access-Control-Allow-Methods": "GET, OPTIONS",
        "Access-Control-Allow-Private-Network": "true",
      };

      if (req.method === "OPTIONS") {
        res.writeHead(204, corsHeaders);
        res.end();
        return;
      }

      if (url.pathname !== "/callback") {
        res.writeHead(404, corsHeaders);
        res.end("Not found");
        return;
      }

      const code = url.searchParams.get("code");
      const returnedState = url.searchParams.get("state");
      const error = url.searchParams.get("error");

      // Direct OAuth redirect (browser we opened, localhost redirect_uri):
      // respond with human-facing HTML.
      if (returnedState === stateLocal) {
        if (error || !code) {
          res.writeHead(200, { "Content-Type": "text/html" });
          res.end(
            "<html><body><h2>Authorization denied.</h2><p>You can close this window.</p></body></html>"
          );
          finish(new Error(`Authorization denied: ${error ?? "no code returned"}`));
          return;
        }
        res.writeHead(200, { "Content-Type": "text/html" });
        res.end(
          "<html><body><h2>Logged in!</h2><p>You can close this window and return to the terminal.</p></body></html>"
        );
        finish(null, { code, redirectUri: LOCAL_REDIRECT_URI });
        return;
      }

      // Notify fetch from the hosted /oauth/cli page (hosted redirect_uri):
      // respond with JSON for the page's script.
      if (returnedState === stateRemote && code) {
        res.writeHead(200, { ...corsHeaders, "Content-Type": "application/json" });
        res.end(JSON.stringify({ ok: true }));
        finish(null, { code, redirectUri: HOSTED_REDIRECT_URI });
        return;
      }

      res.writeHead(400, { ...corsHeaders, "Content-Type": "application/json" });
      res.end(JSON.stringify({ ok: false }));
    });

    const timeout = setTimeout(() => {
      finish(new Error("Timed out waiting for authorization."));
    }, 5 * 60 * 1000);

    function finish(err: Error | null, result?: AuthCode) {
      if (done) return;
      done = true;
      clearTimeout(timeout);
      server.close();
      rl?.close();
      if (err) reject(err);
      else resolve(result!);
    }

    // If the port is taken (another login, another tool), the paste
    // prompt still works — don't fail the whole flow.
    server.on("error", () => {});
    server.listen(CALLBACK_PORT);

    rl = readline.createInterface({
      input: process.stdin,
      output: process.stdout,
    });
    rl.question("Paste code here if prompted > ", (answer) => {
      const input = answer.trim();
      if (!input) {
        finish(new Error("No code provided."));
        return;
      }
      const [code, pastedState] = input.split("#");
      if (!code || pastedState !== stateRemote) {
        finish(new Error("Invalid code. Run `clawnify login` to try again."));
        return;
      }
      finish(null, { code, redirectUri: HOSTED_REDIRECT_URI });
    });
  });
}

export async function loginCommand(): Promise<void> {
  const { codeVerifier: verifier, codeChallenge: challenge } = generatePkce();
  const stateLocal = crypto.randomBytes(16).toString("hex");
  const stateRemote = crypto.randomBytes(16).toString("hex");

  const buildAuthUrl = (redirectUri: string, state: string): string => {
    const url = new URL(OAUTH_AUTHORIZE_URL);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("client_id", OAUTH_CLIENT_ID);
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("code_challenge", challenge);
    url.searchParams.set("code_challenge_method", "S256");
    url.searchParams.set("scope", "openid email");
    url.searchParams.set("state", state);
    return url.toString();
  };

  // The browser we open completes via the localhost redirect (seamless);
  // the printed URL works from any browser via the hosted code page.
  const localAuthUrl = buildAuthUrl(LOCAL_REDIRECT_URI, stateLocal);
  const hostedAuthUrl = buildAuthUrl(HOSTED_REDIRECT_URI, stateRemote);

  console.log("Opening browser to authorize...");
  console.log("\nBrowser didn't open? Use the url below to sign in:\n");
  console.log(`  ${hostedAuthUrl}\n`);
  open(localAuthUrl).catch(() => {
    // No browser available (SSH/headless) — the printed URL is the fallback
  });

  const { code, redirectUri } = await waitForCode(stateLocal, stateRemote);

  const tokenRes = await fetch(OAUTH_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      client_id: OAUTH_CLIENT_ID,
      redirect_uri: redirectUri,
      code_verifier: verifier,
    }),
  });

  if (!tokenRes.ok) {
    const err = await tokenRes.text();
    throw new Error(`Token exchange failed: ${err}`);
  }

  const result = (await tokenRes.json()) as {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    token_type: string;
  };

  // Decode JWT to get email
  let email = "unknown";
  try {
    const payload = JSON.parse(
      Buffer.from(result.access_token.split(".")[1], "base64").toString()
    );
    email = payload.email || "unknown";
  } catch {
    // ignore decode errors
  }

  const expiresAt = Math.floor(Date.now() / 1000) + result.expires_in;

  // Org linking is lazy: the first command that needs an org prompts once
  // (ensureOrg in lib/api.ts) and persists it. `clawnify org use` switches.
  saveAuth({
    access_token: result.access_token,
    refresh_token: result.refresh_token,
    expires_at: expiresAt,
  });

  console.log(`Logged in as ${email}`);
}
